import { ApiStatusFilter } from '@/modules/broadcast/contexts/BroadcastFilterContext';
import type { BroadcastSortDirection } from '@/modules/broadcast/contexts/BroadcastFilterContext';
import { DATASOURCE_TYPES_BY_TAB } from '@/modules/collections/constants';
import { DatasourceTab } from '@/modules/collections/types';
import { ConversationStatusType, Sort } from '@/shared/types/chat';
import { DatasourceType } from '@/shared/types/collections';
import type { SortDirection } from '@/shared/types/common';
import {
  IMPROVEMENTS_PAGINATION_LIMIT,
  INCREASED_PAGINATION_LIMIT,
  INFINITE_SCROLLING_LIMIT,
  PAGINATION_LIMIT,
  TABLE_PAGINATION,
} from '@/util/constants';

export const MAX_FETCHED_DOCUMENTS = 2000;

export const rulesEndpoints = Object.freeze({
  rules: (desk_id: string) =>
    `/www/api/v1/desks/${desk_id}/rules?limit=${INCREASED_PAGINATION_LIMIT}`,
  rule: (desk_id: string, rule_id: string) => `/www/api/v1/desks/${desk_id}/rules/${rule_id}`,
  reorder: (desk_id: string, rule_id: string) =>
    `/www/api/v1/desks/${desk_id}/rules/${rule_id}/reorder`,
  checkName: (desk_id: string, name: string, parentParams?: Record<string, string>) => {
    const params = new URLSearchParams({ name, ...(parentParams ?? {}) });
    return `/www/api/v1/desks/${desk_id}/rules/check_name?${params}`;
  },
});

export const brainsEndpoints = Object.freeze({
  brains: `/www/api/v1/brains?limit=${PAGINATION_LIMIT}&sort=-last_trained&verbose=true`,
  brain: (id: string) => `/www/api/v1/brains/${id ?? '-'}`,
  checkName: (name: string, parentParams?: Record<string, string>) => {
    const params = new URLSearchParams({ name, ...(parentParams ?? {}) });
    return `/www/api/v1/brains/check_name?${params}`;
  },
  onboarding: `/www/api/v1/onboarding/logs`,
  templates: (lang: string) => `/base-templates/${lang}.json`,
});

export const accountsEndpoints = Object.freeze({
  accounts: '/www/api/v1/accounts',
  account: '/www/api/v1/account',
  checkSlug: (id: string) => `/www/api/v1/accounts/check_slug?slug=${id}`,
  agents: '/www/api/v1/account/agents',
  transfer: '/www/api/v1/account/change-ownership',
  leave: '/www/api/v1/account/leave',
});

export const billingEndpoints = Object.freeze({
  plans: '/www/api/v1/plans',
  subscription: '/www/api/v1/stripe/subscription',
  customer: '/www/api/v1/stripe/customer',
  taxId: '/www/api/v1/stripe/tax_id',
  billingState: '/www/api/v1/stripe/billing-state',
  paymentMethods: '/www/api/v1/stripe/payment_methods',
  attachPaymentMethod: (pmId: string) => `/www/api/v1/stripe/payment_methods/${pmId}/attach`,
  detachPaymentMethod: (pmId: string) => `/www/api/v1/stripe/payment_methods/${pmId}/detach`,
  pastInvoices: `/www/api/v1/stripe/invoices/past`,
  upcomingInvoices: `/www/api/v1/stripe/invoices/upcoming`,
  redeemPromoCode: '/www/api/v1/stripe/promotion_codes',
});

export const businessHoursEndpoints = {
  businessHours: (desk_id: string) => `/www/api/v1/desks/${desk_id}/business-hours`,
  businessHour: (desk_id: string, business_hours_id: string) =>
    `/www/api/v1/desks/${desk_id}/business-hours/${business_hours_id}`,
  checkName: (desk_id: string, name: string, parentParams?: Record<string, string>) => {
    const params = new URLSearchParams({ name, ...(parentParams ?? {}) });
    return `/www/api/v1/desks/${desk_id}/business-hours/check_name?${params}`;
  },
} as const;

export const insightsEndpoints = Object.freeze({
  insights: (
    brainId: string,
    cursor?: string,
    filter?: string,
    nameDirection?: SortDirection,
    typeDirection?: SortDirection,
    updatedDirection?: SortDirection
  ) => {
    const getSortDirection = (
      nameDirection?: SortDirection,
      typeDirection?: SortDirection,
      updatedDirection?: SortDirection
    ) => {
      if (nameDirection === 'asc') {
        return 'name';
      }
      if (nameDirection === 'desc') {
        return '-name';
      }
      if (typeDirection === 'asc') {
        return 'type';
      }
      if (typeDirection === 'desc') {
        return '-type';
      }
      if (updatedDirection === 'asc') {
        return 'updated';
      }
      if (updatedDirection === 'desc') {
        return '-updated';
      }
      return '-created';
    };
    const queryParams = new URLSearchParams({
      limit: `${TABLE_PAGINATION}`,
      sort: getSortDirection(nameDirection, typeDirection, updatedDirection),
      verbose: 'true',
    });

    if (cursor) {
      queryParams.append('next_cursor', cursor);
    }
    if (filter) {
      queryParams.append('name', filter);
    }
    const queryString = queryParams.toString();
    return `/www/api/v1/brains/${brainId}/insights?${queryString}`;
  },
  insight: (brainId: string, insightId: string) =>
    `/www/api/v1/brains/${brainId}/insights/${insightId}`,
  createInsight: (brainId: string) => `/www/api/v1/brains/${brainId}/insights`,
  updateInsight: (brainId: string, insightId: string) =>
    `/www/api/v1/brains/${brainId}/insights/${insightId}`,
  deleteInsight: (brainId: string, insightId: string) =>
    `/www/api/v1/brains/${brainId}/insights/${insightId}`,
  testInsight: (brainId: string) => `/www/api/v1/brains/${brainId}/insights/test`,
  validate: (brainId: string) => `/www/api/v1/brains/${brainId}/insights/validate`,
  checkName: (brainId: string, name: string, parentParams?: Record<string, string>) => {
    const params = new URLSearchParams({ name, ...(parentParams ?? {}) });
    return `/www/api/v1/brains/${brainId}/insights/check_name?${params}`;
  },
});

export const collectionsEndpoints = Object.freeze({
  collections: '/www/api/v1/collections?limit=150&verbose=true',
  collection: (id: string) => `/www/api/v1/collections/${id ?? '-'}`,
  checkName: (name: string, parentParams?: Record<string, string>) => {
    const params = new URLSearchParams({ name, ...(parentParams ?? {}) });
    return `/www/api/v1/collections/check_name?${params}`;
  },
});

const getBroadcastSortParam = (direction?: BroadcastSortDirection) =>
  direction === 'asc' ? 'created' : '-created';

export const broadcastsEndpoints = Object.freeze({
  broadcasts: (
    cursor?: string,
    filter?: string,
    status?: ApiStatusFilter,
    sortDirection?: BroadcastSortDirection
  ) => {
    const queryParams = new URLSearchParams({
      limit: `${TABLE_PAGINATION}`,
      sort: getBroadcastSortParam(sortDirection),
      verbose: 'true',
    });

    if (cursor) {
      queryParams.append('next_cursor', cursor);
    }
    if (filter) {
      queryParams.append('name', filter);
    }
    if (status) {
      queryParams.append('status', status);
    }

    const queryString = queryParams.toString();
    return `/www/api/v1/broadcasts?${queryString}`;
  },
  broadcast: (id: string) => `/www/api/v1/broadcasts/${id ?? '-'}`,
  checkName: (name: string, parentParams?: Record<string, string>) => {
    const params = new URLSearchParams({ name, ...(parentParams ?? {}) });
    return `/www/api/v1/broadcasts/check_name?${params}`;
  },
});

export const whatsappTemplatesEndpoints = Object.freeze({
  list: (deskId: string, integrationId: string) =>
    `/www/api/v1/desks/${deskId}/integrations/${integrationId}/whatsapp/templates/?limit=${PAGINATION_LIMIT}`,
});

export const campaignsEndpoints = Object.freeze({
  campaigns: (
    cursor?: string,
    filter?: string,
    limit?: number,
    sort?: string,
    status?: string,
    channels?: string[],
    aiAgents?: string[]
  ) => {
    const queryParams = new URLSearchParams({
      limit: `${limit ?? TABLE_PAGINATION}`,
      sort: sort || '-created',
      verbose: 'true',
    });

    if (cursor) {
      queryParams.append('next_cursor', cursor);
    }
    if (filter) {
      queryParams.append('name', filter);
    }
    if (status) {
      queryParams.append('status', status);
    }
    if (channels?.length) {
      channels.forEach((ch) => queryParams.append('channels', ch));
    }
    if (aiAgents?.length) {
      aiAgents.forEach((id) => queryParams.append('brain_parent_ids', id));
    }

    const queryString = queryParams.toString();
    return `/www/api/v1/campaigns?${queryString}`;
  },
  campaign: (id: string) => `/www/api/v1/campaigns/${id ?? '-'}`,
  campaignAnalytics: (id: string) => `/www/api/v1/campaigns/${id ?? '-'}/analytics`,
  campaignSubscribers: (
    campaignId: string,
    params?: {
      cursor?: string;
      limit?: number;
      q?: string;
      status?: string;
      error_type?: string;
      channels?: string[];
      sort?: string;
    }
  ) => {
    const queryParams = new URLSearchParams({
      limit: `${params?.limit ?? TABLE_PAGINATION}`,
      sort: params?.sort || '-updated',
      verbose: 'true',
    });
    if (params?.cursor) {
      queryParams.append('next_cursor', params.cursor);
    }
    if (params?.q) {
      queryParams.append('q', params.q);
    }
    if (params?.status) {
      queryParams.append('status', params.status);
    }
    if (params?.error_type) {
      queryParams.append('error_type', params.error_type);
    }
    if (params?.channels?.length) {
      params.channels.forEach((ch) => queryParams.append('channels', ch));
    }
    const queryString = queryParams.toString();
    return `/www/api/v1/campaigns/${campaignId}/subscribers?${queryString}`;
  },
  campaignSubscribersUpsert: (campaignId: string, replace?: boolean) =>
    `/www/api/v1/campaigns/${campaignId}/subscribers/bulk-upsert${replace ? '?replace=true' : ''}`,
  /**
   * Legacy Tier-A-only verify (schema checks). Use when no campaign exists yet
   * (pre-creation dry-check). Returns the same SubscriberError[] shape as the
   * campaign-scoped variant.
   */
  campaignSubscribersVerify: '/www/api/v1/campaigns/verify-subscribers',
  /**
   * Campaign-scoped verify (Tier A + Tier B). Runs the full bulk-upsert
   * pipeline against the given campaign and rolls back — catches cross-tenant
   * contact_ids, Flavor-2 REJECTs, and constraint violations that schema-only
   * Tier A cannot see. Use whenever a campaign_id is known for the strongest
   * guarantee that the subsequent bulk-upsert will succeed.
   */
  campaignSubscribersVerifyScoped: (campaignId: string) =>
    `/www/api/v1/campaigns/${campaignId}/subscribers/verify`,
  campaignSenders: (campaignId: string, status?: string) => {
    const queryParams = new URLSearchParams();
    if (status) {
      queryParams.append('status', status);
    }
    return `/www/api/v1/campaigns/${campaignId}/senders?verbose=true`;
  },
  campaignSender: (campaignId: string, senderId: string) =>
    `/www/api/v1/campaigns/${campaignId}/senders/${senderId}`,
  checkName: (name: string, parentParams?: Record<string, string>) => {
    const params = new URLSearchParams({ name, ...(parentParams ?? {}) });
    return `/www/api/v1/campaigns/check_name?${params}`;
  },
  campaignSubscriber: (campaignId: string, subscriberId: string) =>
    `/www/api/v1/campaigns/${campaignId}/subscribers/${subscriberId}`,
  stopCampaignSubscriber: (campaignId: string, subscriberId: string) =>
    `/www/api/v1/campaigns/${campaignId}/subscribers/${subscriberId}/stop`,
  resumeCampaignSubscriber: (campaignId: string, subscriberId: string) =>
    `/www/api/v1/campaigns/${campaignId}/subscribers/${subscriberId}/resume`,
  reevaluateCampaignSubscriber: (campaignId: string, subscriberId: string) =>
    `/www/api/v1/campaigns/${campaignId}/subscribers/${subscriberId}/reevaluate`,
  suggestColumnMappings: '/www/api/v1/campaigns/suggest-column-mappings',
});

export const subscribersEndpoints = Object.freeze({
  subscribers: (id: string, cursor?: string, filter?: string, filterStatus = null) => {
    const queryParams = new URLSearchParams({
      limit: `${INFINITE_SCROLLING_LIMIT}`,
      sort: 'external_id',
    });
    if (cursor) {
      queryParams.append('next_cursor', cursor);
    }
    if (filter) {
      queryParams.append('external_id', filter);
    }
    if (filterStatus) {
      queryParams.append('status', filterStatus);
    }
    const queryString = queryParams.toString();
    return `/www/api/v1/broadcasts/${id}/subscribers?${queryString}`;
  },
  subscribersUpsert: (id: string) => `/www/api/v1/broadcasts/${id}/subscribers/bulk-upsert`,
  subscribersVerify: `/www/api/v1/broadcasts/verify-subscribers`,
});

type ListConversations = {
  id: string;
  status?: ConversationStatusType;
  sort?: Sort;
  cursor?: string;
  agent_id?: string;
  department_id?: string;
  brain_id?: string;
  view_id?: string;
};

export const conversationsEndpoints = Object.freeze({
  conversations: ({
    id,
    status,
    sort,
    cursor,
    agent_id,
    department_id,
    brain_id,
    view_id,
  }: ListConversations) => {
    const queryParams = new URLSearchParams();

    if (cursor) {
      queryParams.append('next_cursor', cursor);
    }
    queryParams.append('sort', sort ?? '-last_message_at');
    queryParams.append('limit', INFINITE_SCROLLING_LIMIT.toString());

    if (view_id) {
      queryParams.append('view_id', view_id);
    } else {
      if (agent_id) {
        queryParams.append('agent_id', agent_id);
      }
      if (department_id) {
        queryParams.append('department_id', department_id);
      }
      if (brain_id) {
        queryParams.append('brain_id', brain_id === 'brain' ? 'any' : brain_id);
      }
      if (status) {
        queryParams.append('status', status);
      }
    }

    return `/www/api/v1/desks/${id}/conversations?${queryParams.toString()}`;
  },
  conversation: (id: string, name: string) =>
    `/www/api/v1/desks/${id ?? '-'}/conversations/${name}`,
  typing: (id: string, name: string) =>
    `/www/api/v1/desks/${id ?? '-'}/conversations/${name}/compose`,
  read: (id: string, name: string) => `/www/api/v1/desks/${id ?? '-'}/conversations/${name}/read`,
  // Send outbound message to create a new conversation
  sendOutboundMessage: (deskId: string) =>
    `/www/api/v1/desks/${deskId ?? '-'}/conversations/outbound`,
});

export const commentsEndpoints = Object.freeze({
  comments: (deskId: string, sessionId: string) =>
    `/www/api/v1/desks/${deskId}/conversations/${sessionId}/comments`,
  comment: (deskId: string, sessionId: string, commentId: string) =>
    `/www/api/v1/desks/${deskId}/conversations/${sessionId}/comments/${commentId}`,
});

export const conversationsUtilsEndpoints = Object.freeze({
  downloadHistory: (id: string, timezone?: string) =>
    `/www/sessions/${id}/history?timezone=${timezone}`,
  translateLogs: (id: string, language: string) =>
    `/www/sessions/${id}/translate?language=${language}`,
  summarize: (id: string) => `/www/sessions/${id}/summarize`,
});

export const datasourceEndpoints = Object.freeze({
  datasources: (collectionId: string) =>
    `/www/api/v1/collections/${collectionId}/datasources?limit=${PAGINATION_LIMIT}`,
  allDatasources: (collectionId: string) =>
    `/www/api/v1/collections/${collectionId}/datasources?limit=${PAGINATION_LIMIT}`,
  faqDatasources: (collectionId: string) =>
    `/www/api/v1/collections/${collectionId}/datasources?types=${DatasourceType.FAQ}&limit=${PAGINATION_LIMIT}`,
  datasource: (collectionId: string, id: string) =>
    `/www/api/v1/collections/${collectionId}/datasources/${id ?? '-'}`,
  syncDatasource: (collectionId: string, id: string) =>
    `/www/api/v1/collections/${collectionId}/datasources/${id ?? '-'}/sync?force=true`,
  // Endpoint for implementing the folder structure view
  datasourcesDocuments: (
    collectionId: string,
    datasourceTab?: DatasourceTab,
    datasourceId?: string,
    documentId?: string
  ) => {
    const baseUrl = `/www/api/v1/collections/${collectionId}/datasources-documents`;
    const queryParams = new URLSearchParams();

    queryParams.append('limit', `${PAGINATION_LIMIT}`);

    if (datasourceTab) {
      queryParams.append('datasource_types', DATASOURCE_TYPES_BY_TAB[datasourceTab].join(','));
    } else {
      queryParams.append(
        'datasource_types',
        [
          DatasourceType.FAQ,
          DatasourceType.FILES,
          DatasourceType.WEBSITE,
          DatasourceType.ZENDESK_KB,
          DatasourceType.INTERCOM_KB,
          DatasourceType.GDRIVE,
        ].join(',')
      );
    }

    if (datasourceId) {
      queryParams.append('datasource_id', datasourceId);
    }

    if (documentId) {
      queryParams.append('document_id', documentId);
    }

    const queryString = queryParams.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  },
  checkName: (collectionId: string, name: string, parentParams?: Record<string, string>) => {
    const params = new URLSearchParams({ name, ...(parentParams ?? {}) });
    return `/www/api/v1/collections/${collectionId}/datasources/check_name?${params}`;
  },
});

export const desksEndpoints = Object.freeze({
  desks: `/www/api/v1/desks?limit=${PAGINATION_LIMIT}&sort=-updated`,
  desk: (id: string) => `/www/api/v1/desks/${id ?? '-'}`,
  checkName: (name: string, parentParams?: Record<string, string>) => {
    const params = new URLSearchParams({ name, ...(parentParams ?? {}) });
    return `/www/api/v1/desks/check_name?${params}`;
  },
  capacityPolicy: (deskId: string) => `/www/api/v1/desks/${deskId ?? '-'}/capacity-policy`,
  capacityOverrides: (deskId: string) =>
    `/www/api/v1/desks/${deskId ?? '-'}/capacity-policy/overrides`,
  capacityOverride: (deskId: string, userId: string) =>
    `/www/api/v1/desks/${deskId ?? '-'}/capacity-policy/overrides/${userId}`,
});

export const dialogsEndpoints = Object.freeze({
  dialogs: (id: string) => `/www/api/v1/brains/${id}/dialogs?limit=${PAGINATION_LIMIT}`,
  dialog: (id: string, dialogId: string) => `/www/api/v1/brains/${id ?? '-'}/dialogs/${dialogId}`,
  collection: (id: string) => `/www/api/v1/brains/${id}/dialogs/collections`,
  checkName: (brainId: string, name: string, parentParams?: Record<string, string>) => {
    const params = new URLSearchParams({ name, ...(parentParams ?? {}) });
    return `/www/api/v1/brains/${brainId}/dialogs/check_name?${params}`;
  },
  validate: (brainId: string, dialogId: string) =>
    `/www/api/v1/brains/${brainId}/dialogs/${dialogId}/validate`,
});

export const urlValidationEndpoints = Object.freeze({
  locales: (subdomain: string) => `/www/url-validation/${encodeURIComponent(subdomain)}`,
  articles: (subdomain: string, locale: string) =>
    `/www/url-validation/articles/${encodeURIComponent(subdomain)}/${encodeURIComponent(locale)}`,
  categories: (subdomain: string, locale: string) =>
    `/www/url-validation/categories/${encodeURIComponent(subdomain)}/${encodeURIComponent(locale)}`,
});

export const documentsEndpoints = Object.freeze({
  documents: (collectionId: string, datasourceId: string, cursor?: string, filter?: string) => {
    const queryParams = new URLSearchParams({ sort: '-created' });

    if (cursor) {
      queryParams.append('next_cursor', cursor);
    }

    if (filter) {
      queryParams.append('name', filter);
    }
    const queryString = queryParams.toString();

    return `/www/api/v1/collections/${collectionId}/datasources/${datasourceId}/documents${
      queryString ? `?${queryString}` : ''
    }`;
  },
  document: (collectionId: string, datasourceId: string, documentId: string) =>
    `/www/api/v1/collections/${collectionId}/datasources/${datasourceId}/documents/${documentId}`,
  exportDocument: (collectionId: string, datasourceId: string, documentId: string) =>
    `/www/api/v1/collections/${collectionId}/datasources/${datasourceId}/documents/${documentId}/download`,
  previewDocument: (collectionId: string, datasourceId: string, documentId: string) =>
    `/www/api/v1/collections/${collectionId}/datasources/${datasourceId}/documents/${documentId}/preview`,
  // New endpoint for getting all documents of a specific type directly from a collection
  collectionDocuments: (
    collectionId: string,
    datasourceTypes: DatasourceType[],
    cursor?: string,
    filter?: string,
    statusDirection?: 'asc' | 'desc',
    updatedDirection?: 'asc' | 'desc'
  ) => {
    const getSortDirection = (statusDirection, updatedDirection) => {
      if (statusDirection === 'asc') {
        return 'status';
      }
      if (statusDirection === 'desc') {
        return '-status';
      }
      if (updatedDirection === 'desc') {
        return '-updated';
      }
      if (updatedDirection === 'asc') {
        return 'updated';
      }
      return '-created';
    };
    const sortDirection = getSortDirection(statusDirection, updatedDirection);

    const queryParams = new URLSearchParams({
      limit: `${TABLE_PAGINATION}`,
      sort: sortDirection,
      verbose: 'true',
    });

    if (cursor) {
      queryParams.append('next_cursor', cursor);
    }

    if (filter) {
      queryParams.append('name', filter);
    }

    if (datasourceTypes && datasourceTypes.length > 0) {
      queryParams.append('datasource_types', datasourceTypes.join(','));
    }

    return `/www/api/v1/collections/${collectionId}/documents?${queryParams.toString()}`;
  },

  modalDocuments: (collectionId: string) => {
    const queryParams = new URLSearchParams();

    queryParams.append('is_draft', 'true');
    // Match the limit of fetching documents
    queryParams.append('limit', `${MAX_FETCHED_DOCUMENTS}`);

    return `/www/api/v1/collections/${collectionId}/documents?${queryParams.toString()}`;
  },

  draftDocumentsCount: (collectionId: string) => {
    const queryParams = new URLSearchParams();

    queryParams.append('is_draft', 'true');
    queryParams.append('limit', '1'); // Only need count from pagination
    queryParams.append('verbose', 'true');

    return `/www/api/v1/collections/${collectionId}/documents?${queryParams.toString()}`;
  },

  publishDocuments: (collectionId: string) => `/www/api/v1/collections/${collectionId}/publish`,
  draftDocuments: (collectionId: string) => `/www/api/v1/collections/${collectionId}/draft`,

  listDocuments: (collectionId: string, datasourceId: string) => {
    const queryParams = new URLSearchParams({
      limit: `${MAX_FETCHED_DOCUMENTS}`,
      sort: '-created',
      verbose: 'true',
    });
    const queryString = queryParams.toString();

    return `/www/api/v1/collections/${collectionId}/datasources/${datasourceId}/documents?${queryString}`;
  },
  moveDocument: (collectionId: string, datasourceId: string, documentId: string) =>
    `/www/api/v1/collections/${collectionId}/datasources/${datasourceId}/documents/${documentId}/datasource`,
  checkName: (
    collectionId: string,
    datasourceId: string,
    name: string,
    parentParams?: Record<string, string>
  ) => {
    const params = new URLSearchParams({ name, ...(parentParams ?? {}) });
    return `/www/api/v1/collections/${collectionId}/datasources/${datasourceId}/documents/check_name?${params}`;
  },
});

export const entitiesEndpoints = Object.freeze({
  entities: (id: string) => `/www/api/v1/brains/${id ?? '-'}/entities?limit=${PAGINATION_LIMIT}`,
  entity: (id: string, name: string) => `/www/api/v1/brains/${id ?? '-'}/entities/${name ?? '-'}`,
  collection: (id: string) => `/www/api/v1/brains/${id}/entities/collections`,
});

export const integrationsEndpoints = Object.freeze({
  integrations: (deskId: string) => `/www/api/v1/desks/${deskId ?? '-'}/integrations`,
  integration: (deskId: string, id: string) =>
    `/www/api/v1/desks/${deskId}/integrations/${id ?? '-'}`,
  verify: (deskId: string, integrationId: string) =>
    `/www/api/v1/desks/${deskId}/integrations/${integrationId}/verify`,
  generateApiKey: (deskId: string, integrationId: string) =>
    `/www/api/v1/desks/${deskId}/integrations/${integrationId}/generate-api-key`,
  checkName: (deskId: string, name: string, parentParams?: Record<string, string>) => {
    const params = new URLSearchParams({ name, ...(parentParams ?? {}) });
    return `/www/api/v1/desks/${deskId}/integrations/check_name?${params}`;
  },
});

export const recommendationsEndpoints = Object.freeze({
  recommendations: (brainId: string) => `/www/api/v1/brains/${brainId}/recommendations`,
});

export const invitationsEndpoints = Object.freeze({
  invitations: '/www/api/v1/invitations',
  userInvitations: '/www/api/v1/invitations/user',
  accountInvitations: '/www/api/v1/invitations/account',
  deleteInvitations: (email?: string) => `/www/api/v1/invitations?email=${email}`,
});

export const macrosEndpoints = Object.freeze({
  macros: (desk_id: string, limit?: number) =>
    `/www/api/v1/desks/${desk_id}/macros?limit=${limit ?? PAGINATION_LIMIT}`,
  macro: (desk_id: string, macro_id: string) => `/www/api/v1/desks/${desk_id}/macros/${macro_id}`,
  checkName: (desk_id: string, name: string, parentParams?: Record<string, string>) => {
    const params = new URLSearchParams({ name, ...(parentParams ?? {}) });
    return `/www/api/v1/desks/${desk_id}/macros/check_name?${params}`;
  },
});

export const membersEndpoints = Object.freeze({
  members: '/www/api/v1/members',
  updateMember: (userId?: string) => `/www/api/v1/members/${userId}`,
});

export const messagesEndpoints = Object.freeze({
  messages: (id: string, sessionId: string, cursor?: string) => {
    const queryParams = new URLSearchParams({
      limit: `${INFINITE_SCROLLING_LIMIT}`,
      sort: '-created',
    });
    if (cursor) {
      queryParams.append('next_cursor', cursor);
    }
    const queryString = queryParams.toString();
    return `/www/api/v1/desks/${id}/conversations/${sessionId}/messages?${queryString}`;
  },
  message: (id: string, name: string) => `/www/api/v1/desks/${id}/conversations/${name}/messages`,

  uploadUrl: (id: string, sessionId: string, slug: string) =>
    `/www/files/presigned-url?deskId=${id}&sessionId=${sessionId}&slug=${slug}`,

  scheduledMessages: (id: string, sessionId: string) =>
    `/www/api/v1/desks/${id}/conversations/${sessionId}/scheduled-messages`,

  scheduledMessage: (id: string, sessionId: string, scheduledMessageId: string) =>
    `/www/api/v1/desks/${id}/conversations/${sessionId}/scheduled-messages/${scheduledMessageId}`,
});

export const preferencesEndpoints = Object.freeze({
  userPrefs: '/www/api/v1/user/preferences',
  accountPrefs: '/www/api/v1/account/preferences',
  accountUserPrefs: '/www/api/v1/account/preferences/user',
});

// A role permission list longer than this comes back cut, with no total to detect it.
export const ROLE_PERMISSIONS_PAGE_LIMIT = 500;

export const rolesEndpoints = Object.freeze({
  roles: '/www/api/v1/roles',
  role: (role_id: string) => `/www/api/v1/roles/${role_id}`,
  rolePermissionsBase: (role_id: string) => `/www/api/v1/roles/${role_id}/permissions`,
  // Paginates at 100 by default, and a grant missing from the page reads as "No access".
  rolePermissions: (role_id: string) =>
    `/www/api/v1/roles/${role_id}/permissions?limit=${ROLE_PERMISSIONS_PAGE_LIMIT}`,
  rolePermission: (role_id: string, role_permission_id: string) =>
    `/www/api/v1/roles/${role_id}/permissions/${role_permission_id}`,
  checkName: (name: string, parentParams?: Record<string, string>) => {
    const params = new URLSearchParams({ name, ...(parentParams ?? {}) });
    return `/www/api/v1/roles/check_name?${params}`;
  },
});

export const teamsEndpoints = Object.freeze({
  teams: '/www/api/v1/teams',
  team: (team_id: string) => `/www/api/v1/teams/${team_id}`,
  checkName: (name: string, parentParams?: Record<string, string>) => {
    const params = new URLSearchParams({ name, ...(parentParams ?? {}) });
    return `/www/api/v1/teams/check_name?${params}`;
  },
});

export const teamUsersEndpoints = Object.freeze({
  users: (team_id: string) => `/www/api/v1/teams/${team_id}/users`,
  user: (team_id: string, user_id: string) => `/www/api/v1/teams/${team_id}/users/${user_id}`,
});

export const userEndpoints = Object.freeze({
  user: '/www/api/v1/user',
  agent: (id: string) => `/www/api/v1/agents/${id}`,
});

export const usersRolesEndpoints = Object.freeze({
  roles: (user_id: string) => `/www/api/v1/users/${user_id}/roles`,
  role: (user_id: string, role_id: string) => `/www/api/v1/users/${user_id}/roles/${role_id}`,
  userDepartments: (id: string) => `/www/api/v1/users/${id}/departments`,
});

export const versionsEndpoints = Object.freeze({
  versions: (id: string) => `/www/api/v1/brains/${id ?? '-'}/versions?sort=-created&verbose=true`,
  version: (id: string, v: number, force?: boolean) =>
    `/www/api/v1/brains/${id ?? '-'}/versions/${v ?? '-'}${force ? '?force=true' : ''}`,
  diff: (id: string) => `/www/api/v1/brains/${id ?? '-'}/versions/diff`,
  publish: (id: string) => `/www/api/v1/brains/${id ?? '-'}/publish`,
});

export const webhooksEndpoints = Object.freeze({
  webhooks: (id: string) => `/www/api/v1/brains/${id ?? '-'}/webhooks`,
  webhook: (id: string, wId: string) => `/www/api/v1/brains/${id ?? '-'}/webhooks/${wId ?? '-'}`,
  test: (id: string) => `/www/api/v1/brains/${id ?? '-'}/webhooks/test`,
  checkName: (brainId: string, name: string, parentParams?: Record<string, string>) => {
    const params = new URLSearchParams({ name, ...(parentParams ?? {}) });
    return `/www/api/v1/brains/${brainId}/webhooks/check_name?${params}`;
  },
});

const encode = encodeURIComponent;

export const intentsEndpoints = Object.freeze({
  intents: (id: string) => `/www/api/v1/brains/${id ?? '-'}/intents?limit=${PAGINATION_LIMIT}`,
  intent: (id: string, name: string) =>
    `/www/api/v1/brains/${id ?? '-'}/intents/${encode(name) ?? '-'}`,
  collection: (id: string) => `/www/api/v1/brains/${id}/intents/collections`,
});

export const bundlesEndpoints = Object.freeze({
  bundles: (desk_id: string) => `/www/api/v1/desks/${desk_id}/bundles`,
  bundle: (desk_id: string, bundle_id: string) =>
    `/www/api/v1/desks/${desk_id}/bundles/${bundle_id || '-'}`,
  checkName: (desk_id: string, name: string, parentParams?: Record<string, string>) => {
    const params = new URLSearchParams({ name, ...(parentParams ?? {}) });
    return `/www/api/v1/desks/${desk_id}/bundles/check_name?${params}`;
  },
});

export const permissionsEndpoints = Object.freeze({ permissions: '/www/api/v1/permissions/user' });

export const teamsRolesEndpoints = Object.freeze({
  roles: (team_id: string) => `/www/api/v1/teams/${team_id}/roles`,
  role: (team_id: string, role_id: string) => `/www/api/v1/teams/${team_id}/roles/${role_id}`,
});

export const credentialsEndpoints = Object.freeze({
  credentials: '/www/api/v1/credentials',
  credential: (credential_id: string) => `/www/api/v1/credentials/${credential_id}`,
  checkName: (name: string, parentParams?: Record<string, string>) => {
    const params = new URLSearchParams({ name, ...(parentParams ?? {}) });
    return `/www/api/v1/credentials/check_name?${params}`;
  },
});

export const languageModelsEnpoints = {
  languageModels: '/www/api/v1/language-models',
  languageModel: (language_model_id: string) => `/www/api/v1/language-models/${language_model_id}`,
  providers: '/www/api/v1/language-models/providers',
  checkName: (name: string, parentParams?: Record<string, string>) => {
    const params = new URLSearchParams({ name, ...(parentParams ?? {}) });
    return `/www/api/v1/language-models/check_name?${params}`;
  },
} as const;

export const speechModelsEndpoints = {
  speechModels: '/www/api/v1/speech-models',
  speechModel: (speech_model_id: string) => `/www/api/v1/speech-models/${speech_model_id}`,
  providers: '/www/api/v1/speech-models/providers',
  checkName: (name: string, parentParams?: Record<string, string>) => {
    const params = new URLSearchParams({ name, ...(parentParams ?? {}) });
    return `/www/api/v1/speech-models/check_name?${params}`;
  },
} as const;

export const speechStrategyEndpoints = Object.freeze({
  /** Full strategy (GET reads, PATCH replaces, DELETE clears). */
  strategy: (brain_id: string) => `/www/api/v1/brains/${brain_id}/speech-strategy`,
});

export const modelStrategyEndpoints = Object.freeze({
  /** Brain-scoped catalog of valid selectors per bucket. */
  catalog: (brain_id: string) => `/www/api/v1/brains/${brain_id}/model-strategy/models`,
  /** Full strategy (GET reads, PATCH replaces, DELETE clears). */
  strategy: (brain_id: string) => `/www/api/v1/brains/${brain_id}/model-strategy`,
});

export const pluginsEndpoints = {
  plugins: '/www/api/v1/plugins',
  plugin: (plugin_id: string) => `/www/api/v1/plugins/${plugin_id}`,
  checkName: (name: string, parentParams?: Record<string, string>) => {
    const params = new URLSearchParams({ name, ...(parentParams ?? {}) });
    return `/www/api/v1/plugins/check_name?${params}`;
  },
} as const;

export const viewsEndpoints = Object.freeze({
  views: (desk_id: string) => `/www/api/v1/desks/${desk_id}/views`,
  view: (desk_id: string, view_id: string) => `/www/api/v1/desks/${desk_id}/views/${view_id}`,
  checkName: (desk_id: string, name: string, parentParams?: Record<string, string>) => {
    const params = new URLSearchParams({ name, ...(parentParams ?? {}) });
    return `/www/api/v1/desks/${desk_id}/views/check_name?${params}`;
  },
});

export const departmentsEndpoints = Object.freeze({
  departments: (desk_id: string) => `/www/api/v1/desks/${desk_id}/departments`,
  department: (desk_id: string, department_id: string) =>
    `/www/api/v1/desks/${desk_id}/departments/${department_id || '-'}`,
  departmentUsers: (desk_id: string, department_id: string) =>
    `/www/api/v1/desks/${desk_id}/departments/${department_id}/users`,
  departmentUser: (desk_id: string, department_id: string, user_id: string) =>
    `/www/api/v1/desks/${desk_id}/departments/${department_id}/users/${user_id}`,
  checkName: (desk_id: string, name: string, parentParams?: Record<string, string>) => {
    const params = new URLSearchParams({ name, ...(parentParams ?? {}) });
    return `/www/api/v1/desks/${desk_id}/departments/check_name?${params}`;
  },
});

export const simulationsEndpoints = Object.freeze({
  simulations: (brainId: string) =>
    `/www/api/v1/brains/${brainId}/simulations?limit=${PAGINATION_LIMIT}`,
  simulation: (brainId: string, id: string) => `/www/api/v1/brains/${brainId}/simulations/${id}`,
  checkName: (brainId: string, name: string) => {
    const params = new URLSearchParams({ name });
    return `/www/api/v1/brains/${brainId}/simulations/check_name?${params}`;
  },
  runs: (
    brainId: string,
    params?: {
      cursor?: string;
      status?: string[];
      with_fails?: boolean;
      trigger_type?: string;
      sort?: string;
      started_after?: string;
      started_before?: string;
    }
  ) => {
    const qs = new URLSearchParams({ limit: `${TABLE_PAGINATION}`, verbose: 'true' });
    if (params?.cursor) {
      qs.append('next_cursor', params.cursor);
    }
    params?.status?.forEach((s) => qs.append('status', s));
    if (params?.with_fails !== undefined) {
      qs.append('with_fails', String(params.with_fails));
    }
    if (params?.trigger_type) {
      qs.append('trigger_type', params.trigger_type);
    }
    if (params?.sort) {
      qs.append('sort', params.sort);
    }
    if (params?.started_after) {
      qs.append('started_after', params.started_after);
    }
    if (params?.started_before) {
      qs.append('started_before', params.started_before);
    }
    return `/www/api/v1/brains/${brainId}/simulations/runs?${qs}`;
  },
  run: (brainId: string, runId: string) =>
    `/www/api/v1/brains/${brainId}/simulations/runs/${runId}`,
  evaluations: (brainId: string, runId: string) =>
    `/www/api/v1/brains/${brainId}/simulations/runs/${runId}/evaluations?limit=${PAGINATION_LIMIT}`,
  evaluationInsights: (brainId: string, runId: string, evaluationId: string) =>
    `/www/api/v1/brains/${brainId}/simulations/runs/${runId}/evaluations/${evaluationId}/insights`,
  cancelRun: (brainId: string, runId: string) =>
    `/www/api/v1/brains/${brainId}/simulations/runs/${runId}/cancel`,
  schedules: (brainId: string) => `/www/api/v1/brains/${brainId}/simulations/schedules`,
  validate: (brainId: string) => `/www/api/v1/brains/${brainId}/simulations/validate`,
  draft: (brainId: string) => `/www/api/v1/brains/${brainId}/simulations/draft`,
});

export const improvementsEndpoints = Object.freeze({
  improvements: (
    brainId: string,
    params?: { status?: string; severity?: string; sort?: string; cursor?: string }
  ) => {
    const qs = new URLSearchParams({
      limit: `${IMPROVEMENTS_PAGINATION_LIMIT}`,
      sort: params?.sort ?? '-created',
    });
    if (params?.status) {
      qs.append('status', params.status);
    }
    if (params?.severity) {
      qs.append('severity', params.severity);
    }
    if (params?.cursor) {
      qs.append('next_cursor', params.cursor);
    }
    return `/www/api/v1/brains/${brainId}/improvements?${qs}`;
  },
  improvement: (brainId: string, id: string) => `/www/api/v1/brains/${brainId}/improvements/${id}`,
});

export const mcpEndpoints = Object.freeze({
  discover: '/www/mcp/discover',
  test: '/www/mcp/test',
  call: '/www/mcp/call',
  oauthStart: '/www/mcp/oauth/start',
  oauthExchange: '/www/mcp/oauth/exchange',
  register: '/www/mcp/oauth/register',
  mcps: (brainId: string) => `/www/api/v1/brains/${brainId}/mcps`,
  // `mcpId` now also arrives straight from the /mcps/:mcpId route param, so it is encoded — an
  // id containing slashes would otherwise resolve to a different orchestrator path.
  mcp: (brainId: string, mcpId: string) =>
    `/www/api/v1/brains/${brainId}/mcps/${mcpId ? encodeURIComponent(mcpId) : '-'}`,
  checkName: (brainId: string, name: string) => {
    const params = new URLSearchParams({ name });
    return `/www/api/v1/brains/${brainId}/mcps/check_name?${params}`;
  },
});
